import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Grid2, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { IconTrash, IconEdit, IconEye, IconDotsVertical, IconPlus, IconSearch, IconRestore } from "@tabler/icons-react";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import StatusChip from "@components/chip/statusChip";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import CustomIconButton from "@components/button/customIconButton";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { ACTION } from "@utils/constant";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";
import { formatDateTime } from "@utils/formatter";

import { applyFilters, createSearchFilter, createCustomFilter } from "@utils/filter";

const defaultQueryFields = {
  search: "",
  status: null,
};

export default function Inquiry() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const { user } = useAuthentication();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      search: "",
      status: null,
    }
  })

  const [searchQuery, setSearchQuery] = useState(defaultQueryFields)

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(false);

  const [action, setAction] = useState("")
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [selectedRow, setSelectedRow] = useState(null)

  const { isFetching, error, data } = useQuery({
    queryKey: ['getInquiry'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getInquiry),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
    },
  })


  const inquiryStatusOptions = useQuery({
    queryKey: ['getInquiryStatusList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getInquiryStatusList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        statusGuid: item.parameterGuid,
        status: item.valueEng
      }));
    },
  })


  //================================Table================================

  const handleClickStatusChip = (row) => {
    setAction(ACTION.UPDATE_STATUS);
    setSelectedRow(row);
    setOpenConfirmDialog(true);
  }

  const handleClickDelete = () => {
    setAction(ACTION.DELETE);
    setOpenConfirmDialog(true);
    setOpenActionMenu(false);
  }

  const handleClickActions = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenActionMenu(true);
    setSelectedRow(row);
  };

  const renderPopper = () => {
    return (
      <Menu
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={menuAnchorEl}
        open={openActionMenu}
        onClose={()=>setOpenActionMenu(false)}
      >
        <MenuItem
          component={RouterLink}
          to={`view?id=${selectedRow?.inquiryGuid}`}
        >
          <ListItemIcon>
            <IconEye size={20}/>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
      </Menu>
    );
  };

  //=====================================================================

  //================================Filter================================
  
    const handleClickSearch = (data) => {
      setSearchQuery({...data, status: data?.status?.statusGuid})
    }
  
    const handleClickReset = () => {
      reset(defaultQueryFields)
      setSearchQuery(defaultQueryFields)
    }
  
    const filters = [
      createSearchFilter(searchQuery.search, ["fullName", "emailAddress", "contactNumber", "message"]),
      createCustomFilter(searchQuery.status, 'statusGuid'),
    ];
  
    const filteredData = applyFilters(data, filters);
  
    //======================================================================

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <Paper
        sx={{
          p: "20px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <Grid2 container spacing={"20px"}>
          <Grid2 size={{ xs: 12 }}>
            <Typography
              fontSize={"0.9rem"}
              color={"#202020"}
              fontWeight={600}
            >
              Search
            </Typography>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledTextField 
              name="search"
              control={control}
              label="Search"
            />
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <ControlledAutocomplete 
              name="status"
              control={control}
              label="Status"
              loading={inquiryStatusOptions.isFetching}
              options={inquiryStatusOptions.data}
              getOptionLabel={(option) => option.status}
              getOptionKey={(option) => option.statusGuid}
              isOptionEqualToValue={(option, value) => option.statusGuid === value?.statusGuid}
            />
          </Grid2>

          <Grid2 size={{ xs: 12 }} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
            <CustomIconButton aria-label="Reset" color="#999999" onClick={handleClickReset}>
              <IconRestore />
            </CustomIconButton>
            <CustomIconButton aria-label="Search" onClick={handleSubmit(handleClickSearch)}>
              <IconSearch />
            </CustomIconButton>
          </Grid2>
        </Grid2>
      </Paper>
      <Paper
        sx={{
          p: "0px 25px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
        }}
      >
        <DataGrid
          loading={isFetching}
          rows={filteredData}
          localeText={{ noRowsLabel: 'No records found' }}
          getRowId={(row) => row.inquiryGuid}
          columns={[
            {
              field: 'fullName',
              headerName: 'Full Name',
              flex: 1, minWidth: 125,
            },
            {
              field: 'emailAddress',
              headerName: 'Email Address',
              flex: 1, minWidth: 125,
            },
            {
              field: 'contactNumber',
              headerName: 'Contact Number',
              flex: 1, minWidth: 125,
            },
            {
              field: 'message',
              headerName: 'Inquiry',
              flex: 1, minWidth: 125,
            },
            {
              field: 'createdOn',
              headerName: 'Inquire On',
              flex: 1, minWidth: 125,
              valueGetter: (value) => formatDateTime(value)
            },
            {
              field: 'status',
              headerName: 'Status',
              flex: 1, minWidth: 100,
              sortable: false,
              renderCell: (params) => (
                <StatusChip  status={params.row.status.valueEng} clickable onClick={() => handleClickStatusChip(params.row)}/>
              ),
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: (params) => (
                <IconButton aria-label="Actions" onClick={(event) => handleClickActions(event, params.row)}>
                  <IconDotsVertical />
                </IconButton>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,25,50]}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          sx={{ border: 0 }}
        />
        {renderPopper()}
      </Paper>
    </div>
  );
}